/*
 * Obfuscated registration prefill transported in the URL fragment
 * (`#d=<base64url(JSON)>`). The fragment never reaches a server, a Referer
 * header or the error log. This is obfuscation, not encryption.
 */
export const PREFILL_PARAM = 'd';
// Real blobs are ~105 characters; the cap bounds atob/JSON.parse on hostile input
export const PREFILL_MAX_LENGTH = 512;

export enum CounsellingRelation {
	Self = 'SELF_COUNSELLING',
	Relative = 'RELATIVE_COUNSELLING',
	Parental = 'PARENTAL_COUNSELLING'
}

export enum Gender {
	Male = 'MALE',
	Female = 'FEMALE',
	Diverse = 'DIVERSE',
	NotProvided = 'NOT_PROVIDED'
}

// Decoded values, named like the registration form fields
export interface RegistrationPrefill {
	age?: string;
	postCode?: string;
	gender?: Gender;
	counsellingRelation?: CounsellingRelation;
	// Topic id (decimal string) or topic name; resolved against the backend like `tid`
	topic?: string;
	// Referrer / campaign tag, max. 8 alphanumeric characters like `ref`
	ref?: string;
}

// JSON object partners encode into the fragment
export interface RegistrationPrefillInput {
	age?: number | string;
	postcode?: string;
	gender?: string;
	counsellingRelation?: string;
	topic?: number | string;
	ref?: string;
}

const COUNSELLING_RELATION_SUFFIX = '_COUNSELLING';
const MIN_AGE = 0;
const MAX_AGE = 100;
const MAX_TOPIC_LENGTH = 100;
const MAX_REF_LENGTH = 8;

export const parseCounsellingRelation = (
	value: unknown
): CounsellingRelation | null => {
	if (typeof value !== 'string') return null;
	const normalized = value.trim().toUpperCase();
	if (!normalized) return null;
	const relation = normalized.endsWith(COUNSELLING_RELATION_SUFFIX)
		? normalized
		: `${normalized}${COUNSELLING_RELATION_SUFFIX}`;
	return (Object.values(CounsellingRelation) as string[]).includes(relation)
		? (relation as CounsellingRelation)
		: null;
};

export const parseGender = (value: unknown): Gender | null => {
	if (typeof value !== 'string') return null;
	const normalized = value.trim().toUpperCase();
	return (Object.values(Gender) as string[]).includes(normalized)
		? (normalized as Gender)
		: null;
};

export const parseAge = (value: unknown): string | null => {
	let age: number;
	if (typeof value === 'number') {
		age = value;
	} else if (typeof value === 'string' && /^\d{1,3}$/.test(value.trim())) {
		age = Number(value.trim());
	} else {
		return null;
	}
	return Number.isInteger(age) && age >= MIN_AGE && age <= MAX_AGE
		? String(age)
		: null;
};

export const parsePostcode = (value: unknown): string | null => {
	if (typeof value !== 'string') return null;
	const normalized = value.trim();
	return /^\d{5}$/.test(normalized) ? normalized : null;
};

// Only alphanumeric characters are kept, truncated to MAX_REF_LENGTH (as for `?ref=`)
export const parseRef = (value: unknown): string | null => {
	if (typeof value !== 'string') return null;
	const normalized = value
		.replace(/[^a-zA-Z0-9]/g, '')
		.substring(0, MAX_REF_LENGTH);
	return normalized.length > 0 ? normalized : null;
};

export const parseTopic = (value: unknown): string | null => {
	if (typeof value === 'number') {
		return Number.isInteger(value) && value > 0 ? String(value) : null;
	}
	if (typeof value !== 'string') return null;
	const normalized = value.trim();
	return normalized.length > 0 && normalized.length <= MAX_TOPIC_LENGTH
		? normalized
		: null;
};

const base64UrlToUtf8 = (input: string): string => {
	const base64 = input
		.trim()
		.replace(/ /g, '+')
		.replace(/-/g, '+')
		.replace(/_/g, '/')
		.replace(/=+$/, '');
	const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
	return decodeURIComponent(
		atob(padded)
			.split('')
			.map((c) => `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`)
			.join('')
	);
};

const utf8ToBase64Url = (input: string): string =>
	btoa(
		encodeURIComponent(input).replace(/%([0-9A-F]{2})/g, (match, hex) =>
			String.fromCharCode(parseInt(hex, 16))
		)
	)
		.replace(/\+/g, '-')
		.replace(/\//g, '_')
		.replace(/=+$/, '');

export const decodeRegistrationPrefill = (
	encoded: string | null | undefined
): RegistrationPrefill => {
	if (!encoded || encoded.length > PREFILL_MAX_LENGTH) return {};

	let parsed: unknown;
	try {
		parsed = JSON.parse(base64UrlToUtf8(encoded));
	} catch {
		return {};
	}
	if (
		typeof parsed !== 'object' ||
		parsed === null ||
		Array.isArray(parsed)
	) {
		return {};
	}

	// Only the known keys are read; the parsed object is never merged or spread
	const input = parsed as Record<string, unknown>;
	const age = parseAge(input.age);
	const postCode = parsePostcode(input.postcode);
	const gender = parseGender(input.gender);
	const counsellingRelation = parseCounsellingRelation(
		input.counsellingRelation
	);
	const topic = parseTopic(input.topic);
	const ref = parseRef(input.ref);

	return {
		...(age !== null && { age }),
		...(postCode !== null && { postCode }),
		...(gender !== null && { gender }),
		...(counsellingRelation !== null && { counsellingRelation }),
		...(topic !== null && { topic }),
		...(ref !== null && { ref })
	};
};

export const encodeRegistrationPrefill = (
	input: RegistrationPrefillInput
): string => utf8ToBase64Url(JSON.stringify(input));

const hashParams = (hash: string) =>
	new URLSearchParams(hash.replace(/^#/, ''));

// Fragment only: the query string is deliberately not read
export const readRegistrationPrefillParam = (
	hash: string = window.location.hash
): string | null => hashParams(hash).get(PREFILL_PARAM);

export const removePrefillFromHash = (hash: string): string => {
	const params = hashParams(hash);
	params.delete(PREFILL_PARAM);
	const remaining = params.toString();
	return remaining ? `#${remaining}` : '';
};
